import React, { useState } from 'react';
import { Copy, Repeat, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { apiCall } from '../../utils/api';

/** Phase A Sec 7/8 — duplicate + recurring tools on an org's own shift card. */
export default function ShiftToolsButtons({ job, onDone }) {
  const [busy, setBusy] = useState('');
  const [showRecurring, setShowRecurring] = useState(false);
  const [pattern, setPattern] = useState('weekly');
  const [occurrences, setOccurrences] = useState(4);
  const [intervalDays, setIntervalDays] = useState(3);

  const duplicate = async () => {
    setBusy('dup');
    try {
      const d = await apiCall(`/api/org/shifts/${job._id}/duplicate`, {
        method: 'POST',
        body: JSON.stringify({ as_draft: false }),
      });
      toast.success('Shift duplicated', { description: `New shift is ${d.status}` });
      onDone && onDone();
    } catch (e) {
      toast.error(e?.message || 'Duplicate failed');
    } finally { setBusy(''); }
  };

  const createRecurring = async () => {
    setBusy('rec');
    try {
      const d = await apiCall(`/api/org/shifts/${job._id}/recurring`, {
        method: 'POST',
        body: JSON.stringify({
          pattern,
          occurrences: Number(occurrences) || 4,
          interval_days: Number(intervalDays) || 3,
          as_draft: false,
        }),
      });
      toast.success(`${d.created} recurring shifts created (${pattern})`);
      setShowRecurring(false);
      onDone && onDone();
    } catch (e) {
      toast.error(e?.message || 'Recurring creation failed');
    } finally { setBusy(''); }
  };

  return (
    <div className="pt-1">
      <div className="flex gap-2">
        <button
          onClick={duplicate}
          disabled={!!busy}
          className="flex-1 px-3 py-2 bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 hover:bg-indigo-100 transition disabled:opacity-40"
          data-testid={`duplicate-job-btn-${job._id}`}
          title="Duplicate this shift"
        >
          {busy === 'dup' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Copy className="w-3.5 h-3.5" />}
          Duplicate
        </button>
        <button
          onClick={() => setShowRecurring(!showRecurring)}
          disabled={!!busy}
          className="flex-1 px-3 py-2 bg-violet-50 text-violet-700 border border-violet-200 rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 hover:bg-violet-100 transition disabled:opacity-40"
          data-testid={`recurring-job-btn-${job._id}`}
          title="Create recurring copies"
        >
          <Repeat className="w-3.5 h-3.5" />
          Recurring
        </button>
      </div>
      {showRecurring && (
        <div className="mt-2 flex items-center gap-2" data-testid={`recurring-form-${job._id}`}>
          <select
            value={pattern}
            onChange={(e) => setPattern(e.target.value)}
            className="flex-1 px-2 py-1.5 border border-gray-200 rounded-lg text-xs"
            data-testid={`recurring-pattern-${job._id}`}
          >
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="biweekly">Biweekly</option>
            <option value="monthly">Monthly</option>
            <option value="custom">Every N days</option>
          </select>
          {pattern === 'custom' && (
            <input
              type="number" min="1" max="90"
              value={intervalDays}
              onChange={(e) => setIntervalDays(e.target.value)}
              className="w-14 px-2 py-1.5 border border-gray-200 rounded-lg text-xs"
              title="Interval in days"
              data-testid={`recurring-interval-${job._id}`}
            />
          )}
          <input
            type="number" min="1" max="30"
            value={occurrences}
            onChange={(e) => setOccurrences(e.target.value)}
            className="w-16 px-2 py-1.5 border border-gray-200 rounded-lg text-xs"
            data-testid={`recurring-occurrences-${job._id}`}
          />
          <button
            onClick={createRecurring}
            disabled={!!busy}
            className="px-3 py-1.5 bg-violet-600 text-white rounded-lg text-xs font-semibold disabled:opacity-40"
            data-testid={`recurring-create-${job._id}`}
          >
            {busy === 'rec' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Create'}
          </button>
        </div>
      )}
    </div>
  );
}
